import { SourceDataInitials } from '@/constants/SourceDataInitials';
import IComp from '@/models/Comp';
import CompUtils from '@/models/comp/CompUtils';
import ICompDatas from '@/models/comp/ICompDatas';
import { Getters, Mutations, store } from '@/store';
import { newUpsertCompDataRequestByFieldName } from '@/store/2.6/models/IUpsertCompDataRequest';
import QSUtils from '@/utils/QSUtils';
import CompTranslationsManager from './CompTranslationsManager';
import FormsManager from './FormsManager';
import UserSettingsManager from '../UserSettingsManager';

/**
 * Manages comp priorities (aka preferences, aka cascades).
 */
export default {
    /**
     * Run priorities on a field on all comps, identified by the given field id.
     *
     * @param fieldId
     */
    async runForAllCompsOnField(fieldId: number) {
        const allComps = store.getters[Getters.CompAddresses.Comps];
        for (const comp of allComps) {
            if (comp.compNumber === 0 && UserSettingsManager.isSubjectCascadeDisabled()) {
                // Skip if Subject transfer column is locked
                continue;
            }

            // Skip if field value has the initial of 'ff'
            if (checkCompForFFDataByFieldId(comp, fieldId)) {
                continue;
            }

            await this.runOnField(comp, fieldId);
            await CompTranslationsManager.runOnField(comp, fieldId);
        }
    },

    /**
     * Run priorities on a field identified by the given comp and field id.
     * @param comp
     * @param fieldId
     */
    async runOnField(comp: IComp, fieldId: number): Promise<void> {
        const compSourceDatas: ICompDatas = comp.sourceData ?? [];
        const formFields = await FormsManager.getFormFields();

        const fieldData = formFields.find((ff) => ff.fieldId === fieldId);
        if (fieldData) {
            // Clear sourceInitial and value based on fieldId.  Then, it will recalculate
            const request = newUpsertCompDataRequestByFieldName(comp, fieldData.fieldName);
            request.sourceInitial = '';
            request.value = '';
            request.description = 'CompPrioritiesManager.runOnField()';
            store.commit(Mutations.CompAddresses.UpsertCompSourceDataByFieldName, request);

            //Loop through Data Priorities
            const flattenedDataPriorities = fieldData.flattenedDataPriorities.split(' | ');
            for (const source in flattenedDataPriorities) {
                let sourceInitial = flattenedDataPriorities[source];
                if (!sourceInitial) {
                    continue;
                }

                // Custom Value check
                if (sourceInitial.toLowerCase().includes(SourceDataInitials.CustomValue)) {
                    sourceInitial = sourceInitial.replaceAll('"', '');
                    const cvParts = sourceInitial.split(/(?<=^\S+)\s/);

                    const cvIdx = compSourceDatas.findIndex((cd) => cd.fieldName === fieldData.fieldName);
                    if (cvIdx !== -1 && cvParts.length > 1 && cvParts[1] !== '') {
                        cvParts[1] = cvParts[1].replace(/["]+/g, '');

                        const request = newUpsertCompDataRequestByFieldName(comp, fieldData.fieldName);
                        request.fieldId = fieldId;
                        request.fieldRulesApplied = false;
                        request.sourceInitial = SourceDataInitials.CustomValue;
                        request.value = cvParts[1];
                        //request.description = 'CompPrioritiesManager.runOnField(CV)';
                        // console.log(`CompPrioritiesManager.runOnField(), Upserting field[${request.fieldId},${request.fieldName}], initial[${request.sourceInitial}], value[${request.value}]`);
                        store.commit(Mutations.CompAddresses.UpsertCompSourceDataByFieldName, request);
                        break;
                    }
                }

                const feedData = CompUtils.getDataSourceFeedData(comp, sourceInitial);
                if (feedData && feedData.sourceData) {
                    if (QSUtils.isKeyInObject(feedData.sourceData, fieldData.fieldName)) {
                        const feedDataValue = feedData.sourceData[fieldData.fieldName];
                        if (feedDataValue !== '' && feedDataValue !== null) {
                            const itemIdx = compSourceDatas.findIndex((cd) => cd.fieldName === fieldData.fieldName);
                            if (itemIdx !== -1) {
                                const request = newUpsertCompDataRequestByFieldName(comp, fieldData.fieldName);
                                request.fieldId = fieldId;
                                request.fieldRulesApplied = false;
                                request.sourceInitial = sourceInitial.toLowerCase();
                                request.value = feedDataValue;
                                //request.description = `CompPrioritiesManager.runOnField(${sourceInitial})`;
                                // console.log(`CompPrioritiesManager.runOnField(), Upserting field[${request.fieldId},${request.fieldName}], initial[${request.sourceInitial}], value[${request.value}]`);
                                store.commit(Mutations.CompAddresses.UpsertCompSourceDataByFieldName, request);
                                break;
                            }
                        }
                    }
                }
            }
        }
    },
};

function checkCompForFFDataByFieldId(comp: IComp, fieldId: number): boolean {
    if (comp.sourceData) {
        //const compSourceData = comp.sourceData ? _.cloneDeep(comp.sourceData) : [];
        const compData = comp.sourceData.find((f: any) => f.fieldId === fieldId);
        if (compData) {
            const sourceInitial = compData.sourceInitial.toLowerCase();
            if (sourceInitial === SourceDataInitials.FormFiller || (sourceInitial === SourceDataInitials.None && compData.value !== '')) {
                return true;
            }
        }
    }

    return false;
}
